import {
  Switch,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@material-ui/core"
import { useContext, useEffect } from "react"
import { useQuery, useQueryClient } from "react-query"
import { useDebounce } from "use-lodash-debounce"
import { listClients } from "~/actions/clients"
import CounterHeader from "~/components/Common/CounterHeader"
import ThumbnailUpload from "~/components/Common/ThumbnailUpload"
import AppContext from "~/contexts/App"
import CertificationContext from "~/contexts/Certification"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"
import serialize from "~/store/serialize"

import Box from "~/components/Common/Box"
import ClientMultiSelect from "~/components/Common/ClientMultiSelect"
import useStyles from "./styles"

const CertificationDetails = () => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const theme = useTheme()

  const { setPageSubTitle } = useContext(AppContext)
  const phoneView = useMediaQuery(theme.breakpoints.down("sm"))

  const { data: clients } = useQuery("clients", () => listClients())

  const openErrorSnackbar = useOpenErrorSnackbar()
  const {
    certification,
    debouncedDetails,
    setDebouncedDetails,
    certDetailsValues,
    setCertDetailsValues,
    setInitialDetailsContext,
    tryPublish,
    lengthError,
    updateCertMutation,
  } = useContext(CertificationContext)

  const debouncedLocalValues = useDebounce(certDetailsValues, 200)

  //show errors if inputs are empty or more than allow characters
  const descriptionError =
    ((debouncedDetails?.description === "" ||
      debouncedDetails?.description?.length > 2000) &&
      tryPublish) ||
    (lengthError && debouncedDetails?.description?.length > 2000)

  const nameError =
    ((debouncedDetails?.name === "" || debouncedDetails?.name?.length > 50) &&
      tryPublish) ||
    (lengthError && debouncedDetails?.name?.length > 50)

  const headlineError =
    ((debouncedDetails?.headline === "" ||
      debouncedDetails?.headline?.length > 225) &&
      tryPublish) ||
    (lengthError && debouncedDetails?.headline?.length > 255)

  // datetime disbaled if certification is locked
  const disablePicker = certification?.published === 1

  const setThumbnail = (uploadRes, loadingAction, type) => {
    const image = JSON.stringify({
      url: uploadRes.data.url,
      secure_url: uploadRes.data.secure_url,
      original_filename: uploadRes.data.original_filename,
      version: uploadRes.data.version,
      format: uploadRes.data.format,
      public_id: uploadRes.data.public_id,
    })
    const data = {
      [type]: image,
    }
    updateCertMutation.mutate(data, {
      onSuccess: (res) => {
        setTimeout(() => {
          loadingAction(false)
        }, 400)
        serialize("certification", res.data).then((serializedData) => {
          queryClient.setQueryData(
            ["certification", res.data.id],
            (oldData) => {
              oldData.thumbnail = serializedData.thumbnail
              oldData.badge_image = serializedData.badge_image
              oldData.background_image = serializedData.background_image
              return oldData
            },
          )
          return queryClient.setQueryData("certifications", (oldData) => {
            let newArray = []
            newArray = oldData.map((x) => {
              if (x.id !== serializedData.id) {
                return x
              } else {
                x[type] = serializedData[type]
                return x
              }
            })
            return [...newArray]
          })
        })
      },
      onError: (err) => {
        console.error(err)
        loadingAction(false)
        openErrorSnackbar(err, "Error Updating Thumbnail")
      },
    })
  }

  // //set certification subtitle to empty string
  useEffect(() => {
    setPageSubTitle("")
  }, [])

  // // set learning path details to compoment state
  useEffect(() => {
    if (certification?.id) {
      setCertDetailsValues({
        name: certification.name,
        description: certification.description || "",
        headline: certification.headline || "",
        is_public: certification?.is_public ? true : false,
        lock_order: certification?.lock_order ? true : false,
        footer_description: certification?.footer_description || "",
        for_me_description: certification?.for_me_description || "",
        clients: certification?.clients || [],
      })
      setInitialDetailsContext({
        name: certification.name,
        description: certification.description || "",
        headline: certification.headline || "",
        is_public: certification?.is_public ? true : false,
        lock_order: certification?.lock_order ? true : false,
        footer_description: certification?.footer_description || "",
        for_me_description: certification?.for_me_description || "",
        clients: certification?.clients || [],
      })
    }
  }, [certification])

  // //set details context state from this component state using debounce
  useEffect(() => {
    setDebouncedDetails(certDetailsValues)
  }, [debouncedLocalValues])

  const updateClients = (newClients) => {
    setCertDetailsValues((oldState) => ({
      ...oldState,
      clients: newClients,
    }))
  }

  return (
    <Box className={classes.container}>
      <Typography variant="h3" color="primary" className={classes.title}>
        Certification Details
      </Typography>
      <div className={classes.twoFieldContainer}>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <CounterHeader
            title="Certification Name"
            value={debouncedDetails?.name}
            max={50}
          />
          <TextField
            value={certDetailsValues?.name || ""}
            onChange={(e) => {
              setCertDetailsValues((oldState) => ({
                ...oldState,
                name: e.target.value,
              }))
            }}
            style={{ marginBottom: "10px" }}
            InputProps={{
              classes: {
                root: classes.inputBackgroundColor,
              },
            }}
            variant="filled"
            size="small"
            fullWidth
          />
          {nameError ? (
            <Typography className={classes.requiredText}>
              Name is required and it has to be less than 50 characters
            </Typography>
          ) : null}
        </Box>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <CounterHeader
            title="Headline"
            value={debouncedDetails?.headline}
            max={100}
          />
          <TextField
            value={certDetailsValues?.headline || ""}
            onChange={(e) => {
              setCertDetailsValues((oldState) => ({
                ...oldState,
                headline: e.target.value,
              }))
            }}
            style={{ marginBottom: "8px" }}
            InputProps={{
              classes: {
                root: classes.inputDescription,
              },
            }}
            variant="filled"
            size="small"
            fullWidth
          />
          {headlineError ? (
            <Typography className={classes.requiredText}>
              Headline is required and it has to be less than 225 characters
            </Typography>
          ) : null}
        </Box>
      </div>
      <div className={classes.twoFieldContainer}>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <Box marginTop="8px">
            <CounterHeader
              title="Certification Description"
              value={debouncedDetails?.description}
              max={2000}
            />
            <TextField
              value={certDetailsValues.description || ""}
              multiline
              maxRows={10}
              onChange={(e) => {
                setCertDetailsValues((oldState) => ({
                  ...oldState,
                  description: e.target.value,
                }))
              }}
              InputProps={{
                disableUnderline: true,
                classes: {
                  root: classes.inputDescription,
                },
              }}
              size="small"
              fullWidth
            />
            {descriptionError ? (
              <Typography className={classes.requiredText}>
                Description is required and it has to be less than 2000
                characters
              </Typography>
            ) : null}
          </Box>
        </Box>
        <Box display="flex" flexDirection={"column"} width={"100%"}>
          <Box marginTop="8px">
            <CounterHeader
              title="Footer Description"
              value={debouncedDetails?.footer_description}
              max={2000}
            />
            <TextField
              value={certDetailsValues.footer_description || ""}
              multiline
              maxRows={10}
              onChange={(e) => {
                setCertDetailsValues((oldState) => ({
                  ...oldState,
                  footer_description: e.target.value,
                }))
              }}
              InputProps={{
                disableUnderline: true,
                classes: {
                  root: classes.inputDescription,
                },
              }}
              size="small"
              fullWidth
            />
          </Box>
        </Box>
      </div>
      {/* <DateTime disablePicker={disablePicker} /> */}
      <Box marginTop="8px">
        <CounterHeader
          title="Is This For Me"
          value={debouncedDetails?.for_me_description}
          max={2000}
        />
        <TextField
          value={certDetailsValues.for_me_description || ""}
          multiline
          maxRows={10}
          onChange={(e) => {
            setCertDetailsValues((oldState) => ({
              ...oldState,
              for_me_description: e.target.value,
            }))
          }}
          InputProps={{
            disableUnderline: true,
            classes: {
              root: classes.inputDescription,
            },
          }}
          size="small"
          fullWidth
        />
      </Box>
      <Box>
        <Typography color="secondary" className={classes.inputLabel}>
          Lock Order
        </Typography>
        <Switch
          onChange={(e) => {
            setCertDetailsValues((oldState) => ({
              ...oldState,
              lock_order: e.target.checked,
            }))
          }}
          value={Boolean(certDetailsValues?.lock_order)}
          checked={Boolean(certDetailsValues?.lock_order)}
        />
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        marginBottom="8px"
        flexWrap="wrap"
      >
        <Box className={classes.thumbnailSectionContainer}>
          <Box className={classes.thumbnailContainer}>
            <Typography color="secondary" className={classes.inputLabel}>
              Asset
            </Typography>
            <ThumbnailUpload
              title="Drop thumbnail image here"
              cloudinaryUploadPreset={
                import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
              }
              value={certification?.thumbnail}
              onUpload={setThumbnail}
              type="thumbnail"
            />
            {Object.keys(certification?.thumbnail)?.length === 0 &&
            tryPublish ? (
              <Typography className={classes.requiredText}>
                Thumbnail is required
              </Typography>
            ) : null}
          </Box>

          <Box className={classes.thumbnailContainer}>
            <Typography color="secondary" className={classes.inputLabel}>
              Badge
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="center">
              <ThumbnailUpload
                title="Drop badge here"
                cloudinaryUploadPreset={
                  import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
                }
                value={certification?.badge_image}
                type="badge_image"
                onUpload={setThumbnail}
              />
            </Box>
            {/* {Object.keys(certification?.badge_image)?.length === 0 && tryPublish ? (
            <Typography className={classes.requiredText}>
              Badge is required
            </Typography>
          ) : null} */}
          </Box>
        </Box>
      </Box>

      <Box className={classes.thumbnailContainer} marginTop="16px">
        <Typography color="secondary" className={classes.inputLabel}>
          Background Image
        </Typography>
        <ThumbnailUpload
          title="Drop background image here"
          cloudinaryUploadPreset={
            import.meta.env.VITE_CLOUDINARY_CLOUD_THUMBNAIL_UPLOAD
          }
          value={certification?.background_image}
          onUpload={setThumbnail}
          type="background_image"
        />
        {/* {Object.keys(certification?.background_image)?.length === 0 &&
        tryPublish ? (
          <Typography className={classes.requiredText}>
            Cover Image is required
          </Typography>
        ) : null} */}
      </Box>

      <ClientMultiSelect
        label="Clients"
        id="clients"
        options={clients ? [...clients] : []}
        className="min-h-[32px]"
        handleChange={updateClients}
        selected={certDetailsValues?.clients || []}
      />

      {/* <Box marginTop="8px" display="flex" alignItems="center">
        <Typography color="secondary" className={classes.inputLabel}>
          Private
        </Typography>
        <Switch
          onChange={(e) => {
            setCertDetailsValues((oldState) => ({
              ...oldState,
              is_public: e.target.checked,
            }))
          }}
          value={certDetailsValues?.is_public}
          checked={certDetailsValues?.is_public}
        />
        <Typography color="secondary" className={classes.inputLabel}>
          Public
        </Typography>
      </Box> */}
    </Box>
  )
}

export default CertificationDetails
